import React, { useMemo } from 'react';
import { ExternalLink, Mail, MessageCircle, Phone, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LegalPageKey } from '../platform/legalConfig';
import { usePublicLegalConfiguration } from '../platform/legalConfig';
import { getSafeLinkAttributes } from '../platform/safeLinks';

type PublicLegalNavigationProps = {
  activePageKey?: LegalPageKey | null;
  variant?: 'panel' | 'section';
};

const orderedLegalPageKeys: LegalPageKey[] = [
  'contacts',
  'legalNotice',
  'termsAndConditions',
  'purchaseConditions',
  'cancellationAndRefunds',
  'privacyPolicy',
  'cookiesPolicy',
  'disputeResolution',
  'complaintsBook',
];

const legalPageLabelKeys: Record<LegalPageKey, string> = {
  contacts: 'legalNavigationContacts',
  legalNotice: 'legalNavigationLegalNotice',
  termsAndConditions: 'legalNavigationTermsAndConditions',
  purchaseConditions: 'legalNavigationPurchaseConditions',
  cancellationAndRefunds: 'legalNavigationCancellationAndRefunds',
  privacyPolicy: 'legalNavigationPrivacyPolicy',
  cookiesPolicy: 'legalNavigationCookiesPolicy',
  disputeResolution: 'legalNavigationDisputeResolution',
  complaintsBook: 'legalNavigationComplaintsBook',
};

const contactChannelLabelKeys = {
  general: 'legalNavigationGeneralContact',
  support: 'legalNavigationSupportContact',
  privacy: 'legalNavigationPrivacyContact',
  complaints: 'legalNavigationComplaintsContact',
} as const;

const PublicLegalNavigation: React.FC<PublicLegalNavigationProps> = ({
  activePageKey = null,
  variant = 'panel',
}) => {
  const { t } = useTranslation();
  const legalConfiguration = usePublicLegalConfiguration();

  const legalLinks = useMemo(
    () =>
      orderedLegalPageKeys.map(key => ({
        key,
        href: legalConfiguration.legalPages[key],
        label: t(legalPageLabelKeys[key]),
      })),
    [legalConfiguration.legalPages, t]
  );

  const externalLinks = useMemo(
    () => [
      {
        href: legalConfiguration.externalUrls.disputeResolutionEntity,
        label: t('legalNavigationExternalRalEntity'),
      },
      {
        href: legalConfiguration.externalUrls.complaintsBook,
        label: t('legalNavigationExternalComplaintsBook'),
      },
    ],
    [legalConfiguration.externalUrls, t]
  );

  const contactChannelLinks = useMemo(
    () => {
      const links = (Object.keys(contactChannelLabelKeys) as Array<keyof typeof contactChannelLabelKeys>)
        .map(key => ({
          key,
          label: t(contactChannelLabelKeys[key]),
          email: legalConfiguration.contacts[key].email,
          href: legalConfiguration.contacts[key].mailtoHref,
        }))
        .filter(link => link.email && link.href);

      const uniqueEmails = new Set(links.map(link => link.email.trim().toLowerCase()));

      return uniqueEmails.size === 1 ? links.filter(link => link.key === 'general') : links;
    },
    [legalConfiguration.contacts, t]
  );

  if (variant === 'section') {
    return (
      <div className="space-y-4">
        <nav
          aria-label={t('legalNavigationAriaLabel')}
          className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4"
        >
          {legalLinks.map(link => (
            <a
              key={link.key}
              href={link.href}
              {...getSafeLinkAttributes(link.href, 'legal')}
              className="inline-flex min-h-10 items-center rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium text-slate-800 transition hover:border-action-300 hover:bg-action-50 hover:text-action-800"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div>
          <p className="text-xs font-semibold uppercase text-action-700">
            {t('legalNavigationContactsTitle')}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {contactChannelLinks.map(link => (
              <a
                key={link.key}
                href={link.href}
                aria-label={`${link.label}: ${link.email}`}
                title={link.email}
                className="inline-flex min-h-10 items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-800 transition hover:bg-sky-100"
              >
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-slate-700">{link.label}</span>
                  <span className="block break-all text-xs text-slate-600">{link.email}</span>
                </span>
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase text-action-700">
            {t('legalNavigationResourcesTitle')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {externalLinks.map(link => (
              <a
                key={link.href}
                href={link.href}
                {...getSafeLinkAttributes(link.href, 'external')}
                className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium text-slate-800 transition hover:border-action-300 hover:bg-action-50 hover:text-action-800"
              >
                {link.label}
                <ExternalLink className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 lg:sticky lg:top-5 lg:self-start">
      <nav
        aria-label={t('legalNavigationAriaLabel')}
        className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
      >
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-950">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          {t('legalNavigationTitle')}
        </div>
        <div className="space-y-1">
          {legalLinks.map(link => {
            const active = activePageKey === link.key;

            return (
              <a
                key={link.key}
                href={link.href}
                {...getSafeLinkAttributes(link.href, 'legal')}
                aria-current={active ? 'page' : undefined}
                className={`block rounded-lg px-3 py-2 text-sm font-medium transition ${
                  active
                    ? 'bg-action-100 text-action-800'
                    : 'text-slate-700 hover:bg-stone-100 hover:text-slate-950'
                }`}
              >
                {link.label}
              </a>
            );
          })}
        </div>
      </nav>

      <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-950">
          {t('legalNavigationContactsTitle')}
        </h3>
        <div className="space-y-2">
          {contactChannelLinks.map(link => (
            <a
              key={link.key}
              href={link.href}
              aria-label={`${link.label}: ${link.email}`}
              title={link.email}
              className="flex min-h-10 items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-800 hover:bg-sky-100"
            >
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-slate-700">{link.label}</span>
                <span className="block break-all text-xs text-slate-600">{link.email}</span>
              </span>
            </a>
          ))}

          {legalConfiguration.contacts.phonePublished &&
          legalConfiguration.contacts.phoneHref &&
          legalConfiguration.contacts.phone ? (
            <a
              href={legalConfiguration.contacts.phoneHref}
              className="flex min-h-10 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-slate-800 hover:bg-emerald-100"
            >
              <Phone className="h-4 w-4 shrink-0 text-emerald-600" />
              <span>{legalConfiguration.contacts.phone}</span>
            </a>
          ) : null}

          {legalConfiguration.contacts.whatsappEnabled && legalConfiguration.contacts.whatsappUrl ? (
            <a
              href={legalConfiguration.contacts.whatsappUrl}
              {...getSafeLinkAttributes(legalConfiguration.contacts.whatsappUrl, 'external')}
              className="flex min-h-10 items-center gap-2 rounded-lg border border-action-200 bg-action-50 px-3 py-2 text-sm text-slate-800 hover:bg-action-100"
            >
              <MessageCircle className="h-4 w-4 shrink-0 text-action-600" />
              <span>{t('whatsapp')}</span>
              <ExternalLink className="ml-auto h-4 w-4 shrink-0 text-action-600" />
            </a>
          ) : null}
        </div>
      </section>

      <section className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-950">
          {t('legalNavigationResourcesTitle')}
        </h3>
        <div className="space-y-2">
          {externalLinks.map(link => (
            <a
              key={link.href}
              href={link.href}
              {...getSafeLinkAttributes(link.href, 'external')}
              className="inline-flex min-h-10 w-full items-center gap-2 rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium text-slate-800 transition hover:border-action-300 hover:bg-action-50 hover:text-action-800"
            >
              <span className="min-w-0 flex-1">{link.label}</span>
              <ExternalLink className="h-4 w-4 shrink-0" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PublicLegalNavigation;
